import { HTTP_STATUS } from '@/constants';
import { handleAuthenticationError } from '@/features/auth/authErrorHandler.ts';

type TokenGetter = (forceRefresh?: boolean) => Promise<string | null>;

let getAuthToken: TokenGetter | null = null;

export const setFetchClientTokenGetter = (getter: TokenGetter) => {
  getAuthToken = getter;
};

const addAuthHeader = async (headers: Headers, forceRefresh: boolean = false) => {
  if (!getAuthToken) return;
  const token = await getAuthToken(forceRefresh);
  if (token) headers.set('Authorization', `Bearer ${token}`);
};

/**
 * Authenticated fetch wrapper.
 * - Injects Bearer token on every request.
 * - On 401: refreshes token and retries once.
 * - On non-ok responses: throws an HttpError so TanStack Query
 *   can handle retries and error state correctly.
 */
export const fetchClient = async (
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> => {
  const buildHeaders = async (forceRefresh: boolean = false) => {
    const headers = new Headers(init.headers);
    await addAuthHeader(headers, forceRefresh);
    return headers;
  };

  const doRequest = async (forceRefresh: boolean = false) =>
    fetch(input, { ...init, headers: await buildHeaders(forceRefresh) });

  const response = await doRequest();

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    const retryResponse = await doRequest(true);
    if (!retryResponse.ok) {
      if (retryResponse.status === HTTP_STATUS.UNAUTHORIZED) {
        // Both attempts returned 401 — the token is genuinely invalid.
        // Trigger the global auth error handler (force-refresh + logout if needed).
        await handleAuthenticationError(retryResponse);
      }
      throw new HttpError(retryResponse);
    }
    return retryResponse;
  }

  if (!response.ok) throw new HttpError(response);
  return response;
};

export class HttpError extends Error {
  constructor(public readonly response: Response) {
    super(`HTTP ${response.status}: ${response.statusText}`);
    this.name = 'HttpError';
  }

  async getErrorMessage(): Promise<string> {
    try {
      const contentType = this.response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await this.response.json();
        return (
          errorData.message || errorData.detail || errorData.error || JSON.stringify(errorData)
        );
      }
      const text = await this.response.text();
      return text || `${this.response.status} ${this.response.statusText}`;
    } catch {
      return `${this.response.status} ${this.response.statusText}`;
    }
  }
}
